import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { GsLayout, GsHeader, GsSidebar, ToastProvider, useDarkMode, GsErrorBoundary } from '@gs/ui';
import type { SidebarItem } from '@gs/ui';
import {
  LayoutDashboard,
  LayoutGrid,
  BarChart2,
  BookOpen,
  Settings2,
} from 'lucide-react';
import { useAuth, GsRole, hasRole } from '@gs/auth';

import MobileBottomNav from './components/MobileBottomNav';
import { ALL_APPS } from './data/apps';
import DashboardPage from './pages/DashboardPage';
import AppsPage from './pages/AppsPage';
import PlaceholderPage from './pages/PlaceholderPage';
import UsersPage from './pages/UsersPage';

const navItems: SidebarItem[] = [
  { key: 'dash', label: 'Mon tableau de bord', icon: LayoutDashboard, path: '/' },
  { key: 'apps', label: 'Toutes les apps', icon: LayoutGrid, path: '/apps', badge: ALL_APPS.length },
  { key: 'analytics', label: 'Analytique', icon: BarChart2, path: '/analytique' },
  { key: 'docs', label: 'Documentation', icon: BookOpen, path: '/docs' },
];

const adminItems: SidebarItem[] = [
  {
    key: 'gs-admin',
    label: 'GS-Admin',
    icon: Settings2,
    path: 'https://gs-admin-d4hvgqhsgud4e3b0.canadacentral-01.azurewebsites.net',
    externalUrl: true,
  },
];

function hubPageTitle(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'Tableau de bord';
  if (pathname.startsWith('/apps')) return 'Applications';
  if (pathname.startsWith('/analytique')) return 'Analytique';
  if (pathname.startsWith('/docs')) return 'Documentation';
  if (pathname.startsWith('/utilisateurs')) return 'Utilisateurs';
  if (pathname.startsWith('/parametres')) return 'Paramètres';
  return 'GS-Hub';
}

function gsRoleLabel(role: GsRole): string {
  switch (role) {
    case GsRole.ADMIN:
      return 'Administrateur';
    case GsRole.GESTIONNAIRE:
      return 'Gestionnaire';
    default:
      return 'Utilisateur';
  }
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const { user, isLoading, login, logout } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fbfbfb] dark:bg-slate-900">
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center">
            <img src="/logo-gs.png" alt="GS" className="h-16 w-16 object-contain dark:hidden" />
            <img src="/logo-gs-white.png" alt="" className="hidden h-16 w-16 object-contain dark:block" />
          </div>
          <h1 className="mb-2 text-xl font-semibold text-slate-800 dark:text-slate-100">Groupe Signalisation</h1>
          <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
            Connectez-vous avec votre compte @groupesignalisation.ca
          </p>
          <button
            type="button"
            onClick={() => void login()}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 dark:bg-slate-600 dark:hover:bg-slate-500 px-6 py-3 text-[14px] font-semibold text-white transition-colors"
          >
            Se connecter avec Microsoft
          </button>
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <GsLayout
        header={<GsHeader pageTitle={hubPageTitle(location.pathname)} />}
        sidebar={
          <GsSidebar
            variant="light"
            headerLogoSrc="/logo-gs.png"
            headerLogoSrcDark="/logo-gs-white.png"
            appName="GS-Hub"
            mainSectionLabel="Navigation"
            adminSectionLabel="Admin"
            items={navItems}
            adminItems={adminItems}
            isAdmin={hasRole(user, GsRole.ADMIN)}
            activePath={location.pathname}
            onNavigate={(path) => navigate(path)}
            bottomUser={{
              name: user.name,
              email: user.email,
              role: gsRoleLabel(user.role),
              initials: user.initials,
            }}
            isDark={isDark}
            onToggleDark={toggleDark}
            onLogout={() => void logout()}
            appVersion="0.1.0"
          />
        }
        mobileNav={<MobileBottomNav />}
      >
        <GsErrorBoundary>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/apps" element={<AppsPage />} />
            <Route path="/analytique" element={<PlaceholderPage title="Analytique" />} />
            <Route path="/docs" element={<PlaceholderPage title="Documentation" />} />
            <Route path="/utilisateurs" element={<UsersPage />} />
            <Route path="/parametres" element={<PlaceholderPage title="Paramètres" />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </GsErrorBoundary>
      </GsLayout>
    </ToastProvider>
  );
}
